// Homepage + /features copy and screenshot slots. Single source for React pages AND the prerenderer.
// To use real screenshots: drop files in frontend/public/screenshots/ and set `src` below (e.g. "/screenshots/map-route.png").
export const SCREENSHOTS = {
  mapRoute: {
    src: "/screenshots/map-route.webp",
    alt: "WanderlyMe map view showing a day's itinerary as numbered stops connected by an optimised walking route across the city",
    label: "Map view · Day 2 route",
  },
  collab: {
    src: "/screenshots/collab.webp",
    alt: "WanderlyMe collaborative trip view with two travellers editing the same itinerary, voting on activities and leaving comments in real time",
    label: "Collaborative trip · live",
  },
  verified: {
    src: "/screenshots/verified.webp",
    fit: "contain",
    alt: "WanderlyMe place card showing a Verified badge with Google rating, opening hours and address pulled from live Google Places data",
    label: "Verified place card",
  },
  budget: {
    src: "/screenshots/budget.webp",
    alt: "WanderlyMe budget planner showing category spend versus total budget with live currency conversion to AUD",
    label: "Budget planner",
  },
  closure: {
    src: "/screenshots/closure.webp",
    alt: "WanderlyMe closure guard flagging an attraction closed on the planned weekday and suggesting open alternatives",
    label: "Closure guard",
  },
  calendar: {
    src: "/screenshots/calendar.webp",
    alt: "WanderlyMe Add to Calendar export placing each itinerary day into a phone calendar",
    label: "Calendar export",
  },
};

export const HOME = {
  description:
    "Plan trips together in real time, with daily routes optimised on a live map and every place verified against Google data. Not just another AI itinerary.",
  eyebrow: "Not just another AI itinerary",
  h1a: "Travel ",
  h1em: "planned around you",
  h1b: ".",
  sub:
    "Plan with the people you're travelling with — live, in one shared trip. Every day is sequenced into a route that makes sense on the map, and every place is checked against live Google data before it reaches your itinerary.",
  cta: "Plan my trip",
  ctaSecondary: "See how it works",
  features: [
    {
      key: "collab",
      title: "Plan it together",
      body: "Invite whoever you're travelling with; everyone edits the same trip, live. Vote on activities, leave comments and lock the final plan when you agree.",
    },
    {
      key: "route",
      title: "Routes that actually work",
      body: "Each day is sequenced so you're not zig-zagging across the city — with real travel times between stops and a one-tap optimiser when plans change.",
    },
    {
      key: "verified",
      title: "Verified, not guessed",
      body: "Every place is checked against live Google Places data for existence, location and opening hours. Anything we can't confirm is labelled, never hidden.",
    },
  ],
  proof: [
    ["Live", "multiplayer editing with votes and comments"],
    ["Mapbox", "travel times between every stop"],
    ["Google Places", "verification on every venue"],
    ["Closure guard", "for the exact weekdays you travel"],
  ],
};

export const FEATURES_PAGE = {
  path: "/features",
  title: "How WanderlyMe Works — Plan Together, Smart Routes, Verified Places",
  description:
    "See how WanderlyMe works: plan a trip together in real time, get each day's stops sequenced on a live map, and know every venue, opening hour and location is checked against live Google Places data — not generated from memory.",
  h1: "How WanderlyMe works",
  intro:
    "A chatbot can write an itinerary. WanderlyMe gives you a trip you can share, follow on a map and actually trust. Here's what happens behind the scenes.",
  sections: [
    {
      shot: "collab",
      heading: "Plan together",
      body: "Share one link and everyone you're travelling with lands in the same trip. Each person can add places, suggest swaps, vote and comment — and everyone sees each other's changes as they happen, so the plan stops living in a group chat. When you agree, lock it and the final itinerary goes to the whole group.",
    },
    {
      shot: "mapRoute",
      heading: "Routes that make sense",
      body: "Each day's stops are sequenced so you aren't crossing the city twice, with real walking, driving or transit times between them. You see the whole day drawn on a live map, and if a booking or the weather changes, one tap re-orders the day without breaking fixed reservations or meal times.",
    },
    {
      shot: "verified",
      heading: "Every place verified",
      body: "Venues, opening hours and locations are checked against live Google Places data, not generated from memory. Every attraction, restaurant and hotel is matched to a real listing with its current hours, rating and address. Anything we can't confirm is labelled Unverified or swapped for a real alternative — never quietly made up.",
    },
  ],
  comparison: {
    heading: "Why not just use ChatGPT?",
    intro: "You can ask a chatbot for an itinerary. Here's what you get instead with a WanderlyMe trip.",
    cols: ["A chat transcript", "A WanderlyMe trip"],
    faq: [
      { q: "Why not just use ChatGPT to plan my trip?", a: "A chatbot gives you a transcript: a list of places generated from training data that may be closed, moved or invented, with no map, no sharing and no verification. WanderlyMe saves the trip to your account, verifies every place against live Google Places data, sequences each day into a route on a live map and lets your travel companions edit it with you in real time." },
      { q: "Can I share and edit a trip with the people I'm travelling with?", a: "Yes. Share one link and everyone lands in the same trip. They can add places, suggest swaps, vote and comment, and see each other's changes live. When you agree, lock the plan and the final itinerary is emailed to the whole group." },
      { q: "Are the places in a WanderlyMe itinerary real and open?", a: "Every attraction, restaurant and hotel is matched to a live Google Places listing and checked for existence, location, current opening hours and rating. Anything that cannot be confirmed is labelled Unverified or swapped for a real alternative — never quietly made up." },
    ],
    rows: [
      ["Lives in a chat window; copy-paste it to share, and everyone edits their own version.", "Saved to your account and shared by link — the whole group edits the same trip, live, with votes and comments."],
      ["Places are generated from training data and may be closed, moved or invented.", "Every place is verified against live Google Places data for existence, hours and location, and labelled if it can't be confirmed."],
      ["A list of names with no sense of distance; you work out the order and the map yourself.", "Each day is sequenced into a route on a live map with real travel times, re-optimised in one tap."],
    ],
  },
  ctaHeading: "See it on your own trip",
  ctaBody: "Build a plan in seconds and invite your travel companions — or ask a human to check every line before you book.",
};
